import { useStorage } from '@vueuse/core'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { nanoid } from 'nanoid'
import {
  ExpiresInTimeKey,
  RefreshTokenKey,
  TokenKey,
  getAppId,
  getToken,
  go2LoginPage,
  clearLocalAuthState,
  setExpiresIn,
  setExpiresTimeIn,
  setRefreshToken,
  setToken,
  setClientId,
} from '~/utils/auth'
import {
  shouldSkip401LoginRedirect,
  shouldRetryAsGuestAfter401Clear,
} from '~/utils/auth401Whitelist'

export interface JcOpts {
  unwrapData?: boolean
  responseCode401SignOut?: boolean
  skipToken?: boolean
  needNotFound?: boolean
  skipCaptcha?: boolean // 跳过滑块验证的选项
  skipJcHeaders?: boolean // 跳过 JC 自定义请求头
  /** 白名单 401 清态后的 guest 重试标记，防死循环 */
  auth401Retried?: boolean
}

type JcFetchOptions<T extends NitroFetchRequest = NitroFetchRequest> =
  NitroFetchOptions<T> & {
    jcOpts?: JcOpts
  }

function getJcOpts(options: any): JcOpts {
  const {
    unwrapData,
    responseCode401SignOut = true,
    skipToken,
    needNotFound = false,
    skipCaptcha = false,
    skipJcHeaders = false,
    auth401Retried = false,
  } = (options as any).jcOpts ?? ({} as JcOpts)
  return {
    unwrapData,
    responseCode401SignOut,
    skipToken,
    needNotFound,
    skipCaptcha,
    skipJcHeaders,
    auth401Retried,
  }
}

function throwNotFoundError(message = '资源不存在') {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not Found',
    message,
    fatal: true,
  })
}

function isNeedNotFoundFailed(resData: any) {
  return !resData || !['DETAIL_DATA_NOT_FOUND'].includes(resData.subCode)
}

function setHeaderKeyValue(headers: Headers, key: string, value: string) {
  if (headers.get(key)) {
    return
  }
  headers.set(key, value)
}

function stripAuthorizationHeaders(headers: HeadersInit | undefined) {
  const next = new Headers(headers || {})
  next.delete('Authorization')
  next.delete('authorization')
  return next
}

function getRequestAuthorization(headers: HeadersInit | undefined) {
  if (!headers) return ''
  try {
    return new Headers(headers as HeadersInit).get('Authorization') || ''
  } catch {
    return ''
  }
}

/** 登录回跳后旧请求 401：请求里的 token 已不是当前 cookie，勿清新会话 */
function isStaleAuth401(
  headers: HeadersInit | undefined,
  currentToken?: string | null,
) {
  if (!currentToken) return false
  const auth = getRequestAuthorization(headers)
  if (!auth) return false
  return !auth.includes(currentToken)
}

/**
 * 每次请求从 document.cookie 现读。
 * Nuxt useCookie 每次调用各自独立 ref，跨实例同步依赖 BroadcastChannel；
 * App WebView 常无 BroadcastChannel，plugin 里提前缓存的 readonly ref 会一直是旧值，
 * 导致登录后请求不带 Authorization → 401 → 再次跳原生登录。
 */
function readClientCookie(name: string): string | null | undefined {
  if (!import.meta.client) return undefined
  try {
    const escaped = name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')
    const matched = document.cookie.match(
      new RegExp(`(?:^|; )${escaped}=([^;]*)`),
    )
    if (!matched?.[1]) return null
    return decodeURIComponent(matched[1])
  } catch {
    return null
  }
}

function resolveCookieValue(
  name: string,
  cookieRef: { value: string | null | undefined },
  fallback?: string | null,
) {
  const fromDocument = readClientCookie(name)
  if (fromDocument) return fromDocument
  if (cookieRef.value) return cookieRef.value
  return fallback || ''
}

// ============================================
// 滑块验证状态管理
// ============================================
interface CaptchaRequest {
  request: any
  options: any
  resolve: (value?: any) => void
  reject: (error?: any) => void
}

// ============================================
// 页面级 PageId 管理
// ============================================
interface PageIdMap {
  [key: string]: string
}

export default defineNuxtPlugin((nuxtApp) => {
  const { public: publicConfig } = useRuntimeConfig()
  const clientId = useStorage<string>('clientId', '') || generateClientUid()
  const systemStore = useMySystemStore()
  const route = useRoute()
  const pendingNavRouteName = useState<string | symbol | null | undefined>(
    'pendingNavRouteName',
    () => null,
  )
  const pendingNavFromName = useState<string | symbol | null | undefined>(
    'pendingNavFromName',
    () => null,
  )
  const pendingNavPath = useState<string | null>('pendingNavPath', () => null)
  const pendingNavNeedToken = useState('pendingNavNeedToken', () => false)
  const process401 = useState('process401', () => false)
  const refreshState = useState('refreshState', () => ({
    queue: [] as Array<{
      opts: any
      resolve: (value?: unknown) => void
      reject: (err?: any) => void
    }>,
    isRunning: false,
  }))
  const clientIdInitPromise = import.meta.client
    ? setClientId().catch(() => undefined)
    : Promise.resolve(undefined)

  // useCookie 必须在 await 之前（plugin setup 阶段）取 ref，
  // 否则 onRequest 里 await clientIdInitPromise 后会丢失 Nuxt 上下文
  const prjIdCookie = useCookie('prjId', { readonly: true })
  const tokenCookie = useCookie(TokenKey, { readonly: true })
  const refreshTokenCookie = useCookie(RefreshTokenKey, { readonly: true })
  const expiresTimeInCookie = useCookie(ExpiresInTimeKey, { readonly: true })

  if (import.meta.client) {
    const router = useRouter()
    router.beforeEach((to, from) => {
      pendingNavRouteName.value = to.name ?? null
      pendingNavFromName.value = from.name ?? null
      pendingNavPath.value = to.path ?? null
      pendingNavNeedToken.value = !!to.meta?.needToken
    })
  }

  // ============================================
  // 滑块验证状态 - 全局单例
  // ============================================
  const captchaState = useState('captchaState', () => ({
    visible: false,
    queue: [] as CaptchaRequest[],
    isVerifying: false,
    currentResolve: null as ((uuid: string) => void) | null,
    currentReject: null as ((error?: any) => void) | null,
  }))

  // ============================================
  // 页面级 PageId 管理
  // ============================================
  const pageIdState = useState<PageIdMap>('pageIdState', () => {
    return {}
  })

  // 根据路由获取或生成 PageId
  function getPageId(): string {
    const currentPath = route.path
    const pageIdMap = pageIdState.value

    if (pageIdMap[currentPath]) {
      return pageIdMap[currentPath]
    }

    // 生成新的 PageId 并缓存
    const newPageId = nanoid()
    pageIdMap[currentPath] = newPageId
    return newPageId
  }

  // 验证成功后继续执行队列
  function processCaptchaSuccess(uuid?: string) {
    const state = captchaState.value
    state.isVerifying = false
    state.visible = false

    // 通知等待验证完成的请求继续执行
    if (state.currentResolve) {
      state.currentResolve(uuid || '')
      state.currentResolve = null
      state.currentReject = null
    }

    // 执行队列中的所有请求（重新发起请求）
    const queue = [...state.queue]
    state.queue = []

    queue.forEach((item) => {
      // 重新发起请求，验证成功后服务器会识别跳过验证
      jcFetch(item.request, item.options)
        .then(item.resolve)
        .catch(item.reject)
    })

    // 如果队列还有请求，继续验证
    if (state.queue.length > 0) {
      processCaptchaSuccess()
    }
  }

  // 验证失败或关闭
  function processCaptchaClose() {
    const state = captchaState.value
    state.isVerifying = false
    state.visible = false

    // 拒绝所有等待中的请求
    state.queue.forEach((item) => {
      item.reject(new Error('Captcha verification cancelled'))
    })
    state.queue = []

    if (state.currentReject) {
      state.currentReject(new Error('Captcha verification cancelled'))
      state.currentResolve = null
      state.currentReject = null
    }
  }

  // 暴露给外部的方法
  nuxtApp.provide('captchaState', captchaState)
  nuxtApp.provide('processCaptchaSuccess', processCaptchaSuccess)
  nuxtApp.provide('processCaptchaClose', processCaptchaClose)

  const rawFetch = $fetch.create({
    timeout: 120000,
    baseURL: publicConfig.PROD_CLIENT_PROXY_API as string,
  })

  const jcFetch = $fetch.create({
    timeout: 120000,
    baseURL: publicConfig.PROD_CLIENT_PROXY_API as string,
    onRequest: [
      async ({ options: requestOptions, request }) => {
        if (!clientId.value) {
          await clientIdInitPromise
        }

        const requestUrl = request.toString()
        if (requestUrl.startsWith('/api/') && !requestUrl.startsWith('http')) {
          requestOptions.baseURL = ''
        }

        const jcOpts = getJcOpts(requestOptions)

        // ============================================
        // 添加 JC 自定义请求头（可跳过）
        // ============================================
        if (!jcOpts.skipJcHeaders) {
          // X-JC-ClientId => 使用 clientUid
          if (clientId.value) {
            setHeaderKeyValue(
              requestOptions.headers,
              'X-JC-ClientId',
              clientId.value,
            )
          }

          // X-JC-PageId => 每个页面唯一，同页面共用一个
          setHeaderKeyValue(
            requestOptions.headers,
            'X-JC-PageId',
            getPageId(),
          )

          // X-JC-PageCode => 当前页面的PageCode，没有则为空
          const metaPageCode = route.meta?.pageCode as string
          setHeaderKeyValue(
            requestOptions.headers,
            'X-JC-PageCode',
            metaPageCode || '',
          )

          // X-JC-UserId => 如果登录了，则上报当前用户的userid
          setHeaderKeyValue(
            requestOptions.headers,
            'X-JC-UserId',
            systemStore.userId || '',
          )

          // X-JC-TraceId => 每次请求一个完全独立的随机uuid
          setHeaderKeyValue(
            requestOptions.headers,
            'X-JC-TraceId',
            nanoid(),
          )

          // meta.server = true 时，增加 X-JC-Internal-Token = ws
          if (import.meta.server) {
            setHeaderKeyValue(
              requestOptions.headers,
              'X-JC-Internal-Token',
              'ws',
            )
          }
        }

        setHeaderKeyValue(
          requestOptions.headers,
          'Accept-Language',
          systemStore.language,
        )

        if (clientId.value) {
          setHeaderKeyValue(
            requestOptions.headers,
            'client-uid',
            clientId.value,
          )
        }

        if (requestOptions.method === 'POST') {
          setHeaderKeyValue(requestOptions.headers, 'APP-ID', getAppId())
          setHeaderKeyValue(
            requestOptions.headers,
            'terminal-type',
            systemStore.terminalType,
          )
          setHeaderKeyValue(
            requestOptions.headers,
            'Content-Type',
            'application/json',
          )
          setHeaderKeyValue(
            requestOptions.headers,
            'Access-Control-Allow-Origin',
            '*',
          )

          const prjIdValue = resolveCookieValue('prjId', prjIdCookie)
          if (prjIdValue) {
            const prjIdText = String(prjIdValue)
            setHeaderKeyValue(
              requestOptions.headers,
              'popular-channel',
              prjIdText.indexOf(',') > 0
                ? (prjIdText.split(',')[0] ?? prjIdText)
                : prjIdText,
            )
          }

          const metaPageCode = route.meta?.pageCode as string
          if (metaPageCode) {
            setHeaderKeyValue(requestOptions.headers, 'page-code', metaPageCode)
          }
        }

        if (!requestOptions.headers.get('Authorization') && !jcOpts.skipToken) {
          // systemStore.token：App bridge 刚写入时的兜底（cookie 尚未同步完）
          const cTk = resolveCookieValue(
            TokenKey,
            tokenCookie,
            systemStore.token,
          )
          cTk &&
            setHeaderKeyValue(
              requestOptions.headers,
              'Authorization',
              `Bearer ${cTk}`,
            )
        }
      },
      async (requestOpts) => {
        const distance = 60 * 5 * 1000
        const refreshTokenStr = resolveCookieValue(
          RefreshTokenKey,
          refreshTokenCookie,
        )
        const expiresTimeIn = parseInt(
          String(
            resolveCookieValue(ExpiresInTimeKey, expiresTimeInCookie) || '',
          ),
          10,
        )

        if (
          refreshTokenStr &&
          expiresTimeIn - Date.now() <= distance &&
          expiresTimeIn - Date.now() > 0
        ) {
          const refreshObj = refreshState.value

          if (!refreshObj.isRunning) {
            refreshObj.isRunning = true
            let newAccessToken: string | undefined
            let res
            try {
              res = await rawFetch<{
                code: number
                data: {
                  accessToken: string
                  refreshToken: string
                  expireIn: number
                }
              }>('/auth/refreshToken', {
                method: 'POST',
                body: {
                  refreshToken: refreshTokenStr,
                },
              })
            } catch (error) {
              console.log('refresh token error', error)
            }
            if (res?.code === 0) {
              const { accessToken, expireIn, refreshToken } = res.data
              const newTimestamp = Date.now() + expireIn * 1000
              setToken(accessToken)
              setExpiresIn(expireIn)
              setRefreshToken(refreshToken)
              setExpiresTimeIn(newTimestamp)
              newAccessToken = accessToken
            } else {
              console.log('刷新 token 失败')
            }
            if (
              newAccessToken &&
              requestOpts.options.headers.get('Authorization')
            ) {
              requestOpts.options.headers.set(
                'Authorization',
                `Bearer ${newAccessToken}`,
              )
            }
            refreshObj.queue.forEach((item) => {
              if (
                newAccessToken &&
                item.opts.options.headers.get('Authorization')
              ) {
                item.opts.options.headers.set(
                  'Authorization',
                  `Bearer ${newAccessToken}`,
                )
              }
              item.resolve()
            })
            refreshObj.queue.length = 0
            refreshObj.isRunning = false
          } else {
            await new Promise((resolve, reject) => {
              refreshObj.queue.push({
                opts: requestOpts,
                resolve,
                reject,
              })
            })
          }
        }
      },
    ],
    onResponse: [
      async ({ response, options, request }) => {
        const jcOpts = getJcOpts(options)

        if (jcOpts.needNotFound && isNeedNotFoundFailed(response?._data)) {
          throwNotFoundError(
            response?._data?.describe || response?._data?.msg || '资源不存在',
          )
        }

        if (response._data?.code === 401) {
          console.warn('401 from', response.url, response._data)
          if (!jcOpts.responseCode401SignOut) {
            return
          }
          const locationPath =
            import.meta.client && typeof window !== 'undefined'
              ? window.location.pathname
              : ''
          const skipLoginRedirect = shouldSkip401LoginRedirect({
            currentRoute: route,
            navigationTargetName: pendingNavRouteName.value,
            navigationTargetPath: pendingNavPath.value || locationPath,
            navigationFromName: pendingNavFromName.value,
            targetNeedToken: pendingNavNeedToken.value,
            requestUrl: response.url,
          })
          if (skipLoginRedirect) {
            const skipOpts = {
              currentRoute: route,
              navigationTargetName: pendingNavRouteName.value,
              navigationTargetPath: pendingNavPath.value || locationPath,
              navigationFromName: pendingNavFromName.value,
              targetNeedToken: pendingNavNeedToken.value,
              requestUrl: response.url,
              alreadyRetried: !!jcOpts.auth401Retried,
            }
            // 登录 exchange 后旧飞行中请求 401，勿清刚写入的新 token
            const currentToken =
              getToken() || useMySystemStore().token || ''
            if (isStaleAuth401(options.headers as HeadersInit, currentToken)) {
              console.warn('ignore stale 401 after token refresh', response.url)
              return
            }

            const canRetry = shouldRetryAsGuestAfter401Clear(skipOpts)
            if (canRetry) {
              clearLocalAuthState()
              try {
                const retryResult = await jcFetch(request, {
                  ...options,
                  headers: stripAuthorizationHeaders(
                    options.headers as HeadersInit,
                  ),
                  jcOpts: {
                    ...jcOpts,
                    skipToken: true,
                    auth401Retried: true,
                  },
                })
                response._data = retryResult
              } catch (error) {
                console.warn('401 guest retry failed', response.url, error)
              }
              return
            }

            // 不可 guest 重试（/auth/*、clear-only 等）：当前 cookie token 已被服务端 401
            // 拒绝 → 清本地态。覆盖过期与互踢；stale 401 已在上方 return。
            // 注意：勿再依赖文案匹配，互踢 msg/subcode 常与「登录已超时」不一致。
            clearLocalAuthState()
            return
          }
          if (process401.value) {
            return
          }
          process401.value = true
          try {
            await jcFetch('/logout')
          } catch (error) {
            console.log('登录失败 e', error)
          } finally {
            clearLocalAuthState()
          }
          process401.value = false
          try {
            await go2LoginPage(route.path)
          } catch (error) {
            if (import.meta.client && window) {
              window.location.href = route.path
            }
          }
          return
        }

        // ============================================
        // 处理 418/918 滑块验证
        // ============================================
        if ([418, 918].includes(Number(response.status))) {
          if (jcOpts.skipCaptcha) {
            return
          }

          const state = captchaState.value

          // 如果已经在验证中，将请求加入队列
          if (state.isVerifying) {
            return new Promise((resolve, reject) => {
              state.queue.push({ request, options, resolve, reject })
            })
          }

          // 开始验证流程
          state.isVerifying = true
          state.visible = true

          // 返回 Promise，等待验证完成后重新请求
          return new Promise((resolve, reject) => {
            state.currentResolve = (uuid?: string) => {
              // 验证成功后，重新发起请求
              const retryOptions = { ...options }
              // 如果验证成功返回了 uuid，可以添加到请求头中
              if (uuid) {
                setHeaderKeyValue(
                  retryOptions.headers,
                  'X-JC-Captcha-Token',
                  uuid,
                )
              }
              jcFetch(request, retryOptions)
                .then(resolve)
                .catch(reject)
            }
            state.currentReject = reject

            // 将当前请求加入队列
            state.queue.push({ request, options, resolve, reject })
          })
        }

        if ([0, 200].includes(Number(response._data?.code))) {
          const { unwrapData } = jcOpts
          if (unwrapData) {
            response._data = response._data.data
          }
          return
        }
      },
    ],
    onResponseError({ response, options, error, request }) {
      const jcOpts = getJcOpts(options)
      if (jcOpts.needNotFound) {
        throwNotFoundError(
          response?._data?.describe ||
            response?._data?.message ||
            response?._data?.msg ||
            error?.message ||
            '资源不存在',
        )
      }

      // ============================================
      // 处理 418/918 滑块验证错误
      // ============================================
      if ([418, 918].includes(Number(response?.status))) {
        if (jcOpts.skipCaptcha) {
          return Promise.reject({
            message: error?.message || '需要滑块验证',
            code: 418,
            data: response?._data,
          })
        }

        const state = captchaState.value

        // 如果已经在验证中，将请求加入队列
        if (state.isVerifying) {
          return new Promise((resolve, reject) => {
            state.queue.push({ request, options, resolve, reject })
          })
        }

        // 开始验证流程
        state.isVerifying = true
        state.visible = true

        // 返回 Promise，等待验证完成后重新请求
        return new Promise((resolve, reject) => {
          state.currentResolve = (uuid?: string) => {
            // 验证成功后，重新发起请求
            const retryOptions = { ...options }
            if (uuid) {
              setHeaderKeyValue(
                retryOptions.headers,
                'X-JC-Captcha-Token',
                uuid,
              )
            }
            jcFetch(request, retryOptions)
              .then(resolve)
              .catch(reject)
          }
          state.currentReject = reject

          // 将当前请求加入队列
          state.queue.push({ request, options, resolve, reject })
        })
      }

      if (response && response._data) {
        const { code, describe } = response._data
        return Promise.reject({
          message: describe || '请求失败',
          code: code || 500,
          data: response._data,
        })
      }
      return Promise.reject({
        message: error?.message || '发生未知错误',
        code: 500,
      })
    },
  })

  function jcDataFetch(
    request: NitroFetchRequest,
    options: JcFetchOptions = {},
  ) {
    const jcOpts: JcOpts = {
      unwrapData: true,
      ...(options.jcOpts ?? {}),
    }
    return jcFetch(request, {
      ...options,
      jcOpts,
    })
  }

  nuxtApp.provide('jcFetch', jcFetch)
  nuxtApp.provide('jcDataFetch', jcDataFetch)
})
